import { apiClient, keysFactory, transformApplicantToApi } from '@applyonline/shared'
import { FilteredCreditApplication } from '@applyonline/shared/types'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosRequestConfig } from 'axios'

import { EFinancingProgram } from '@if/core/types'
import { Applicant } from '../types/Schemas/ApplicantSchema'
import { PersonalInformationInput, Prequalification } from '../types/Schemas/PrequalificationSchema'
import { transformPrequalToApi } from './credit-transform'

export interface SubmitCoapplicantDto {
  recaptchaToken: string
  creditApplicationId: string
  coapplicant: Applicant
}

export interface SubmitPrequalificationDto {
  recaptchaToken: string
  prequalification: Prequalification
}

export interface UpdatePrequalificationDto {
  creditApplicationId: string
  recaptchaToken: string
  personalInfoDto: PersonalInformationInput
}

const FINANCING_PROGRAM = EFinancingProgram.Personal

const submitCoapplicant: MutationFunction<FilteredCreditApplication, SubmitCoapplicantDto> = async (
  dto: SubmitCoapplicantDto,
) => {
  const options: AxiosRequestConfig = {
    headers: {
      'x-recaptcha-token': dto.recaptchaToken,
    },
  }
  const coapplicant = transformApplicantToApi(dto.coapplicant)
  const response = await apiClient.post(
    `personal/creditApplication/${dto.creditApplicationId}/coapplicant`,
    coapplicant,
    options,
  )
  return response.data as FilteredCreditApplication
}

export function useSubmitCoapplicant(): [
  MutationFunction<FilteredCreditApplication, SubmitCoapplicantDto>,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: submitCoapplicant,
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.detail(data.id, FINANCING_PROGRAM), data)
      return data
    },
  })

  return [mutateAsync, isPending, reset]
}

const removeCoapplicant: MutationFunction<FilteredCreditApplication, string> = async (creditApplicationId: string) => {
  const response = await apiClient.delete(`personal/creditApplication/${creditApplicationId}/coapplicant`)
  return response.data as FilteredCreditApplication
}

export function useRemoveCoapplicant(): [MutationFunction<FilteredCreditApplication, string>, boolean, () => void] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: removeCoapplicant,
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.detail(data.id, FINANCING_PROGRAM), data)
      return data
    },
  })

  return [mutateAsync, isPending, reset]
}

const submitPrequalification: MutationFunction<FilteredCreditApplication, SubmitPrequalificationDto> = async (
  dto: SubmitPrequalificationDto,
) => {
  const options: AxiosRequestConfig = {
    headers: {
      'x-recaptcha-token': dto.recaptchaToken,
    },
  }
  const prequal = transformPrequalToApi(dto.prequalification)
  const response = await apiClient.post(`personal/creditapplication/prequalify`, prequal, options)
  return response.data as FilteredCreditApplication
}

export function useSubmitPrequalification(): [
  MutationFunction<FilteredCreditApplication, SubmitPrequalificationDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset, isSuccess } = useMutation({
    mutationFn: submitPrequalification,
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.detail(data.id, FINANCING_PROGRAM), data)
      return data
    },
  })

  return [mutateAsync, isPending, isSuccess, reset]
}

const updatePrequalification: MutationFunction<FilteredCreditApplication, UpdatePrequalificationDto> = async (
  dto: UpdatePrequalificationDto,
) => {
  const options: AxiosRequestConfig = {
    headers: {
      'x-recaptcha-token': dto.recaptchaToken,
    },
  }
  const response = await apiClient.put(
    `personal/creditapplication/${dto.creditApplicationId}`,
    dto.personalInfoDto.applicant,
    options,
  )
  return response.data as FilteredCreditApplication
}

export function useUpdatePrequalification(): [
  MutationFunction<FilteredCreditApplication, UpdatePrequalificationDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset, isSuccess } = useMutation({
    mutationFn: updatePrequalification,
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.detail(data.id, FINANCING_PROGRAM), data)
      return data
    },
  })

  return [mutateAsync, isPending, isSuccess, reset]
}
